/**
 * The rules for holding an invite's referrer id between a web signup and the moment that person
 * connects a phone — which is the moment, and the only moment, the referral can be recorded (the
 * service refuses unless the referee's recipient carries both an email and a phone).
 *
 * The gap is deliberately long: someone can sign up today and connect a number tomorrow, so the id
 * lives in `localStorage` rather than in the page or the session. Pure decision functions here, with no
 * DOM and no runtime imports, so they can be unit-tested — the thin storage wrapper is at the bottom.
 *
 * Known limit, accepted: this is bound to a BROWSER. An invitee who signs up on a desktop and then
 * connects their number inside the phone app leaves no way to read this, so that referral is not
 * recorded. Closing it needs the pending referrer stored server-side on the recipient, which the
 * referral service has nowhere to put today.
 */

/** Product-named, not the legacy `orion-web:` prefix (see the note in orion-web/CLAUDE.md). */
export const REFERRAL_STORAGE_KEY = 'symphony-referral';

/** Long enough that a slow onboarding keeps the credit, short enough that a shared machine forgets. */
export const REFERRAL_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export interface StoredReferral {
  /** The sharer's metaSiteId, lowercased. */
  ref: string;
  /** Who this was captured for. '' when the caller could not be identified at capture time. */
  userId: string;
  capturedAt: number;
}

const METASITE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/**
 * Minted referral code: 8 chars of the lowercase base32 alphabet the recipient service mints from
 * (`abcdefghjkmnpqrstuvwxyz23456789` — no 0/o/1/l, which is why `[a-z2-9]` is the right superset).
 * See docs/referral-short-links-plan.md.
 */
const MINTED_REFERRAL_CODE = /^[a-z2-9]{8}$/;
/**
 * Vanity referral code — a handle the owner chose (`ronny.elkayam`), which is also the `<handle>` of a
 * `/me/<handle>` Symphony profile link: 3-30 chars of `[a-z0-9._-]`, starting and ending with a letter or
 * digit, never `..`. The SHAPE only — whether it is reserved or held by anyone is the recipient service's
 * call (`VanityReferralCodes` in the VMR), never this file's. A GUID (36 chars) can never match. Mirrors
 * `isReferralShortCode` in the serverless jot path (`nativeDataEndpointBuilder.ts`); the two must stay
 * identical.
 *
 * **Accepted only with `allowVanity`** — the caller conducted `specs.SymphonyProfileLinks` and is on — and
 * only from OUR OWN routes (`/r/<code>`, `/me/<handle>`: `routeReferralToStore`), never from a generic
 * `?ref=`: the shape is wide enough to admit the campaign tags other sites put there (`producthunt`,
 * `newsletter`). Without it every function here is exactly the pre-vanity behaviour.
 */
const VANITY_REFERRAL_CODE = /^(?!.*\.\.)[a-z0-9][a-z0-9._-]{1,28}[a-z0-9]$/;

export interface CodeShapeOptions {
  /** `specs.SymphonyProfileLinks` is on for the caller: a vanity-shaped code counts as a code. */
  allowVanity?: boolean;
}

function isCodeShaped(normalized: string, { allowVanity = false }: CodeShapeOptions): boolean {
  return MINTED_REFERRAL_CODE.test(normalized) || (allowVanity && VANITY_REFERRAL_CODE.test(normalized));
}

/** A shape only our own links produce — a metaSiteId or a minted code — as opposed to a vanity value. */
function isOwnLinkShape(normalized: string): boolean {
  return METASITE_ID.test(normalized) || MINTED_REFERRAL_CODE.test(normalized);
}

/**
 * A referrer id is either a metaSiteId GUID (the original link shape, still in the wild and valid
 * forever) or a short referral code. Two reasons to check the shape before it ever leaves the client,
 * neither of them authentication — the value is public and client-supplied either way:
 *
 *  - The server resolves a code and parses a GUID, so anything matching neither is a round trip that
 *    can only fail. An exact union (not "anything non-empty") keeps junk `?ref=` values unstored, so
 *    an API rejection stays terminal instead of retrying for the whole 30-day TTL.
 *  - The self-referral guard is a case-sensitive string comparison, so a case-variant of your own
 *    metasite would slip past "you cannot refer yourself". Lowercasing here removes that variant —
 *    and the code alphabet is lowercase-only, so it survives the same normalization untouched.
 */
/**
 * Is this a referral CODE rather than a metaSiteId? The redemption paths need to know, because a code must
 * be resolved to a metasite before `markReferral` will accept it — see `pages/api/referral-claim.ts`.
 * Mirrors `isReferralShortCode` in the serverless jot-attribution path; the two must stay identical.
 */
export function isReferralShortCode(value: string, options: CodeShapeOptions = {}): boolean {
  const id = value.trim().toLowerCase();
  return !METASITE_ID.test(id) && isCodeShaped(id, options);
}

export function normalizeReferrerId(raw: unknown, options: CodeShapeOptions = {}): string | null {
  if (typeof raw !== 'string') return null;
  const id = raw.trim().toLowerCase();
  return METASITE_ID.test(id) || isCodeShaped(id, options) ? id : null;
}

/** What to persist when a `?ref=` invite lands, or null when there is nothing worth keeping. Never vanity. */
export function referralToStore({ raw, userId, now }: { raw: unknown; userId: string; now: number }): StoredReferral | null {
  const ref = normalizeReferrerId(raw);
  if (!ref) return null;
  return { ref, userId, capturedAt: now };
}

/**
 * What to persist when one of OUR OWN routes (`/r/<code>`, `/me/<handle>`) carries the code, or null. The
 * one place a vanity code may be captured, and only with `allowVanity`.
 *
 * `existing` is what is already stored. A still-spendable stored metaSiteId or minted code is KEPT against a
 * different incoming vanity value (only our own links produce those shapes, so it is a real invite); a
 * stored vanity value is never protected — the newer route visit wins, as with any last-touch capture.
 */
export function routeReferralToStore({
  code,
  userId,
  now,
  existing = null,
  allowVanity = false,
}: {
  code: unknown;
  userId: string;
  now: number;
  existing?: StoredReferral | null;
} & CodeShapeOptions): StoredReferral | null {
  const ref = normalizeReferrerId(code, { allowVanity });
  if (!ref) return null;
  const protectsExisting =
    !isOwnLinkShape(ref) && !!existing && existing.ref !== ref && isOwnLinkShape(existing.ref) && !!referralToSpend({ stored: existing, userId, now });
  if (protectsExisting) return null;
  return { ref, userId, capturedAt: now };
}

/**
 * The id to spend now, or null. Rejects an expired record, and one captured for a different signed-in
 * user — otherwise the next person to use this browser would collect a stranger's invite. A record with
 * no userId on it stays spendable: we could not identify the caller at capture, and dropping the credit
 * is the worse of the two errors when the payout is already capped at one per phone, ever.
 */
export function referralToSpend({
  stored,
  userId,
  now,
  allowVanity = false,
}: { stored: StoredReferral | null; userId: string; now: number } & CodeShapeOptions): string | null {
  if (!stored) return null;
  if (!normalizeReferrerId(stored.ref, { allowVanity })) return null;
  if (now - stored.capturedAt > REFERRAL_TTL_MS) return null;
  if (stored.userId && userId && stored.userId !== userId) return null;
  return stored.ref;
}

/** Parses whatever is in storage, tolerating anything a previous version or a user may have left. */
export function parseStoredReferral(rawJson: string | null, options: CodeShapeOptions = {}): StoredReferral | null {
  if (!rawJson) return null;
  try {
    const value = JSON.parse(rawJson) as Partial<StoredReferral>;
    const ref = normalizeReferrerId(value?.ref, options);
    if (!ref || typeof value.capturedAt !== 'number') return null;
    return { ref, userId: typeof value.userId === 'string' ? value.userId : '', capturedAt: value.capturedAt };
  } catch {
    return null;
  }
}

// ---- storage wrapper. Every path is guarded: Safari private mode throws on write, and a referral is
// never worth breaking a signup over.

export function saveReferral(record: StoredReferral): void {
  try {
    window.localStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // No storage (private mode, quota, disabled) — the referral is simply not recorded.
  }
}

export function loadReferral(options: CodeShapeOptions = {}): StoredReferral | null {
  try {
    return parseStoredReferral(window.localStorage.getItem(REFERRAL_STORAGE_KEY), options);
  } catch {
    return null;
  }
}

export function clearReferral(): void {
  try {
    window.localStorage.removeItem(REFERRAL_STORAGE_KEY);
  } catch {
    // Nothing to do — see saveReferral.
  }
}
